import { Chip } from '@crowdex/ui/components/chip';
import { Input } from '@crowdex/ui/components/input';
import { cn } from '@crowdex/ui/lib/utils';
import { ChevronDown, MessageSquare, Minimize2, UserPlus, UsersRound } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SidebarHeader, SidebarItem, SidebarSection } from '@/components/sidebar/sidebar';
import { restorePanel } from '@/lib/desktop-shell/app-panels';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { coreAppSidebarSlot } from '@/lib/desktop-shell/core-app-sidebar-slot';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { useCallLevel } from './call-level';
import {
  CallControls,
  CallConversations,
  CallError,
  CallRequest,
  CallStartIcon,
  CallStatus,
  CallSteps,
  CallTranscript,
  CallView,
  InviteMenu,
} from './call-parts';
import { useLiveCallContext } from './live-call';
import { LiveCallLine } from './live-chip';
import { speakingAssistant } from './live-place';
import type { LiveCall } from './use-live-call';

export { useOpenLive } from './open-live';

/**
 * The Live tab (D28; mobile 25): the call with the voice of the place, large. A stage shows its
 * face, its line (time, who the user talks to, what is on screen, private), its latest words and
 * the call's controls; beside it, what happens during the call: what waits on the user, the
 * conversations it works with, what was said and done, and a field to type to it. Leaving the
 * tab, the call goes on in picture in picture.
 */
export function LivePanel() {
  const context = useLiveCallContext();
  if (context === null || !context.enabled)
    return <p className="p-4 text-muted-foreground text-sm">Live is not available here.</p>;
  return <LiveScreen call={context.call} />;
}

/** The Live tab of a call (the preview shows one of its own). */
export function LiveScreen({ call }: { call: LiveCall }) {
  return (
    // Sized by its column, not the window: the tab can be the panel beside a conversation.
    <section aria-label="Live" className="@container size-full">
      <div className="grid size-full grid-rows-1 gap-4 overflow-y-auto p-6 @max-lg:p-0 @4xl:grid-cols-[minmax(0,1fr)_24rem]">
        <LiveSidebar call={call} />
        <LiveStage call={call} />
        <LiveFeed call={call} />
      </div>
    </section>
  );
}

/** The assistant the call speaks as (or reaches), as the user sees it. */
function speakerTitle(call: LiveCall): string {
  return speakingAssistant(call)?.title ?? 'Assistant';
}

/** An assistant's colour, as its row in the sidebar. */
function AssistantDot({ color }: { color: string }) {
  return (
    <span
      aria-hidden="true"
      className="size-2 shrink-0 rounded-[3px]"
      style={{ backgroundColor: color }}
    />
  );
}

/**
 * A group call's assistants side by side, each in its tile: the one speaking lit, the one
 * preparing an answer marked, the others listening.
 */
function GroupGallery({ call }: { call: LiveCall }) {
  const members = call.group?.members ?? [];
  return (
    <div
      className="absolute inset-0 grid content-center gap-3 px-4 pt-16 pb-36"
      style={{
        gridTemplateColumns: `repeat(${members.length === 4 ? 2 : members.length}, minmax(0, 1fr))`,
      }}
    >
      {members.map((member) => {
        const speaking = call.groupSpeaker === member.id;
        const thinking = call.groupThinking === member.id;
        return (
          <figure
            aria-label={member.title}
            className={cn(
              'relative aspect-[3/4] max-h-full min-h-0 overflow-hidden rounded-xl border bg-background/40 transition-shadow',
              speaking ? 'border-foreground/40 ring-2 ring-foreground/25' : 'border-hairline',
            )}
            key={member.id}
          >
            <CallView
              call={call}
              className="absolute inset-0 rounded-none bg-transparent"
              member={member.id}
            />
            <span className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-surface-overlay px-3 py-2 font-medium text-xs">
              {member.title}
              {speaking && <span className="size-1.5 rounded-full bg-foreground" />}
              {thinking && <span className="font-normal text-muted-foreground">answering…</span>}
            </span>
          </figure>
        );
      })}
    </div>
  );
}

/** The assistant on its stage: its face, the call's line, its words, and the controls. */
function LiveStage({ call }: { call: LiveCall }) {
  const title = speakerTitle(call);
  const store = useDesktopShellStoreApi();
  const opener = useConversationOpener();
  const said = call.transcript.at(-1);
  const swipe = useRef<number | null>(null);
  const showConversation = () => {
    restorePanel(store);
    if (call.assistant !== null) void opener?.openConversation(call.assistant);
  };
  return (
    <div
      className="relative isolate min-h-0 overflow-hidden rounded-panel border bg-card @max-lg:rounded-none @max-lg:border-0 @max-lg:bg-background"
      data-testid="live-stage"
      onTouchEnd={(event) => {
        const start = swipe.current;
        swipe.current = null;
        const end = event.changedTouches[0]?.clientY;
        // A swipe up brings the conversation back over the call (mobile 25).
        if (start !== null && end !== undefined && start - end > 60) showConversation();
      }}
      onTouchStart={(event) => {
        swipe.current = event.touches[0]?.clientY ?? null;
      }}
    >
      <StageRings call={call} />
      {call.group === null ? (
        <CallView call={call} className="absolute inset-0 rounded-none bg-transparent" />
      ) : (
        <GroupGallery call={call} />
      )}
      <button
        aria-label="Minimise the call"
        className="absolute top-5 left-3 z-10 flex size-10 items-center justify-center rounded-control text-foreground hover:bg-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring @lg:hidden"
        onClick={() => restorePanel(store)}
        title="The call goes on in picture in picture"
        type="button"
      >
        <ChevronDown className="size-6" />
      </button>
      <div className="absolute inset-x-0 top-0 flex items-start gap-2 p-4 @max-lg:flex-col @max-lg:items-center @max-lg:pt-6">
        <h2 className="flex items-center gap-1.5 font-semibold text-sm @max-lg:text-base @lg:sr-only">
          {call.group !== null && <UsersRound aria-hidden="true" className="size-3.5" />}
          {call.group?.title ?? title}
        </h2>
        <LiveCallLine call={call} className="min-w-0 flex-1 @max-lg:justify-center" />
        <button
          aria-label="Back to the conversation"
          className="flex size-8 shrink-0 items-center justify-center rounded-control text-muted-foreground hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring @max-lg:hidden"
          onClick={() => restorePanel(store)}
          title="The call goes on in picture in picture"
          type="button"
        >
          <Minimize2 className="size-4" />
        </button>
      </div>
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-5 px-6 pb-8">
        {said !== undefined && (
          <p
            aria-hidden="true"
            className={cn(
              'line-clamp-3 max-w-xl text-balance text-center text-base leading-relaxed',
              said.role === 'user' && 'text-muted-foreground',
            )}
          >
            {said.role === 'user' ? said.text : `« ${said.text} »`}
          </p>
        )}
        <CallStatus call={call} />
        {call.status === 'idle' ? (
          <div className="flex flex-col items-center gap-2">
            <button
              className="flex h-control-lg items-center gap-2 rounded-control bg-primary px-5 font-medium text-primary-foreground text-sm hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => void call.start()}
              type="button"
            >
              <CallStartIcon aria-hidden="true" className="size-4" />
              {`Call ${title}`}
            </button>
            <InviteMenu call={call}>
              <button
                aria-label="Call several assistants together"
                className="flex h-control items-center gap-1.5 rounded-control px-3 text-muted-foreground text-xs hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                type="button"
              >
                <UserPlus aria-hidden="true" className="size-3.5" />
                Call with others…
              </button>
            </InviteMenu>
          </div>
        ) : (
          <CallControls call={call} labelled />
        )}
        {call.assistant !== null && opener !== null && (
          <button
            className="flex h-control items-center gap-2 rounded-control bg-control px-4 text-sm @lg:hidden"
            onClick={showConversation}
            type="button"
          >
            <MessageSquare aria-hidden="true" className="size-4" />
            Show the conversation
          </button>
        )}
      </div>
    </div>
  );
}

/** Rings around the assistant, wider as it speaks louder: flat, no gradient. */
function StageRings({ call }: { call: LiveCall }) {
  const level = useCallLevel();
  const speaking = call.status === 'speaking';
  return (
    <div aria-hidden="true" className="-z-10 absolute inset-0 flex items-center justify-center">
      <span
        className="absolute aspect-square w-[min(60%,15rem)] rounded-full bg-foreground/[0.04] transition-transform duration-150"
        style={{ transform: `scale(${1 + (speaking ? level * 0.25 : 0)})` }}
      />
      <span className="absolute aspect-square w-[min(48%,12rem)] rounded-full bg-foreground/[0.05]" />
    </div>
  );
}

/** What happens during the call, beside the stage. */
function LiveFeed({ call }: { call: LiveCall }) {
  const said = useRef<HTMLDivElement>(null);
  const [typed, setTyped] = useState('');
  const lines = call.transcript.length;
  const steps = call.steps.length;
  useEffect(() => {
    if (lines + steps > 0) said.current?.scrollTo({ top: said.current.scrollHeight });
  }, [lines, steps]);
  const inCall = call.status !== 'idle';
  return (
    <aside
      aria-label="During the call"
      className="hidden min-h-0 flex-col gap-3 rounded-panel border bg-card p-4 text-sm @4xl:flex"
    >
      <h3 className="font-semibold text-sm">During the call</h3>
      {call.assistants.length > 1 && (
        // Who answers: before a call, the one it reaches; in a call, the one it is handed to.
        <ul
          aria-label={call.status === 'idle' ? 'Talk to' : 'Hand the call to'}
          className="flex flex-wrap gap-1.5"
        >
          {call.assistants.map((entry) => {
            const current = call.group === null && entry.id === call.speaker;
            return (
              <li key={entry.id}>
                <Chip onClick={() => call.switchTo(entry.id)} selected={current}>
                  <AssistantDot color={entry.color} />
                  {entry.title}
                </Chip>
              </li>
            );
          })}
        </ul>
      )}
      <CallError call={call} />
      <CallRequest call={call} />
      <CallConversations call={call} className="text-ui" />
      <div className="-mx-1 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-1" ref={said}>
        <CallTranscript call={call} />
        <CallSteps call={call} className="border-hairline border-l pl-3 text-ui" />
      </div>
      {inCall && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const text = typed.trim();
            if (text === '') return;
            call.type(text);
            setTyped('');
          }}
        >
          <Input
            aria-label="Type to the assistant"
            className="h-11 rounded-control border-transparent bg-control px-4 shadow-none"
            onChange={(event) => setTyped(event.target.value)}
            placeholder="Type to the assistant…"
            value={typed}
          />
        </form>
      )}
    </aside>
  );
}

/**
 * The shell sidebar on Live's side of its toggle: the assistants (home first, one to call or hand
 * the call to), the channel of the one on the line, and what it works with.
 */
function LiveSidebar({ call }: { call: LiveCall }) {
  const slot = coreAppSidebarSlot.useElement();
  const opener = useConversationOpener();
  const chatId = useDesktopShellStore((state) => state.chatId);
  if (slot === null || opener === null) return null;
  return createPortal(
    <nav aria-label="Live">
      <SidebarHeader title="Live" />
      {call.assistants.length > 1 && (
        <SidebarSection label={call.status === 'idle' ? 'Talk to' : 'Hand the call to'}>
          {call.assistants.map((entry) => (
            <SidebarItem
              active={call.group === null && entry.id === call.speaker}
              detail={
                call.group !== null
                  ? call.group.members.some((member) => member.id === entry.id)
                    ? 'In the group'
                    : undefined
                  : entry.id === call.speaker && call.status !== 'idle'
                    ? 'On the line'
                    : undefined
              }
              icon={MessageSquare}
              key={entry.id}
              label={entry.title}
              onSelect={() => call.switchTo(entry.id)}
              title={entry.title}
            />
          ))}
        </SidebarSection>
      )}
      {call.assistant !== null && (
        <SidebarSection>
          <SidebarItem
            active={chatId === call.assistant}
            icon={MessageSquare}
            label={call.group?.title ?? speakerTitle(call)}
            onSelect={() => {
              if (call.assistant !== null) void opener.openConversation(call.assistant);
            }}
          />
        </SidebarSection>
      )}
      {call.followed.length > 0 && (
        <SidebarSection label="Conversations it works with">
          {call.followed.map((entry) => (
            <SidebarItem
              active={chatId === entry.conversation}
              icon={MessageSquare}
              key={entry.conversation}
              label={entry.label}
              onSelect={() => void opener.openConversation(entry.conversation)}
              title={entry.label}
            />
          ))}
        </SidebarSection>
      )}
    </nav>,
    slot,
  );
}
